import { useMutation } from '@tanstack/react-query';
import api from '../../lib/api';

/**
 * #1775 — Forgot Password client hooks. Thin react-query mutations over the
 * public `/api/v1/auth/forgot-password/*` endpoints. No auth token is involved
 * (the user is logged out); the email + one-time code/token carry the flow.
 */

/** POST /auth/forgot-password/send-code — initial send AND "Resend Code". */
export function useSendResetCode() {
  return useMutation({
    mutationFn: async (email: string): Promise<void> => {
      await api.post('/auth/forgot-password/send-code', { email });
    },
  });
}

/** POST /auth/forgot-password/verify-code → returns the one-time resetToken. */
export function useVerifyResetCode() {
  return useMutation({
    mutationFn: async (vars: { email: string; code: string }): Promise<string> => {
      const res = await api.post<{ data: { resetToken: string } }>(
        '/auth/forgot-password/verify-code',
        vars,
      );
      return res.data.data.resetToken;
    },
  });
}

/** POST /auth/forgot-password/reset — requires the resetToken from verify. */
export function useResetPassword() {
  return useMutation({
    mutationFn: async (vars: {
      email: string;
      resetToken: string;
      newPassword: string;
      confirmPassword: string;
    }): Promise<void> => {
      await api.post('/auth/forgot-password/reset', vars);
    },
  });
}

/** Pull a friendly message out of an Axios error, with a fallback. */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const msg = (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data
    ?.error?.message;
  return msg || (error instanceof Error ? error.message : '') || fallback;
}
